/* eslint-disable max-statements */
import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import { Message } from '@paypalcorp/worldready-react'
import LogoImage from './logo-image/LogoImage'
import LogoutModal from './LogoutModal'
import OfferTabs from './offer-tabs/OfferTabs'
import StoreDetails from './store-details/StoreDetails'
import StorePageHeader from './store-page-header/StorePageHeader'
import StoreDetailsSidebarCard from './store-sidebar-details/StoreDetailsSidebarCard'
import useBreakpoint from './lib/useBreakpoint'
import useWorldReady from './lib/useWorldReady'
import defaultMerchantLogo from './assets/default-store-logo.svg'
import {
  StorePageType,
  StoreDataType,
  HoneyOfferType,
  HoneyCouponOfferType,
  HoneyDealOfferType
} from './types/StorePageType'
import { sendFptiImpression } from './lib/fpti'
import { SHOPPING_MERCHANT_SCREEN_SHOWN } from './lib/client-constants'
import HoneyOfferDetailsModal from './HoneyOfferDetailsModal'
import getAffiliateLink from './utils/get-affiliate-link'

const StorePage = ({ serverData, isTMPlus }: StorePageType) => {
  const [affiliateVisited, setAffiliateVisited] = useState(false)
  const { formatMessage } = useWorldReady()
  const { isTablet, isMobile } = useBreakpoint()
  const {
    encryptedAccountNumber,
    isUserLoggedIn,
    honeyStoreDetails,
    honeyApiResponse,
    bestHoneyCoupons,
    requestUrl,
    isDW2,
    csrf,
    fptiSessionId
  } = serverData

  const averageSavings: number = get(honeyApiResponse, 'avgSavings30d') || 0

  const unorderedOffers: HoneyOfferType[] = get(
    honeyApiResponse,
    'publicSortedDeals',
    []
  )
  const dealOffers: HoneyDealOfferType[] = []
  const couponOffers: HoneyCouponOfferType[] = []
  const allOfferIdsArray: string[] = []
  const orderedOffers = unorderedOffers.sort(
    (a: HoneyOfferType, b: HoneyOfferType) => {
      if (a.position === b.position) {
        return 0
      } else if (a.position === null) {
        return 1
      } else if (b.position === null) {
        return -1
      } else if (a.position < b.position) {
        return -1
      }
      return 1
    }
  )

  orderedOffers.forEach((offer: HoneyOfferType) => {
    allOfferIdsArray.push(offer.dealId)
    if (offer.type === 'COUPON') {
      couponOffers.push(offer as HoneyCouponOfferType)
    } else {
      dealOffers.push(offer as HoneyDealOfferType)
    }
  })

  const totalCoupons = couponOffers.length
  const totalOffers = orderedOffers.length

  const allOffers = {
    honeyAll: orderedOffers,
    honeyCoupons: couponOffers,
    honeyDeals: dealOffers,
    storeUrl: get(honeyStoreDetails, 'url')
  }

  const storeData: StoreDataType = {
    storeId: honeyStoreDetails.storeId,
    merchantLogo: honeyStoreDetails.logo,
    merchantLabel: honeyStoreDetails.label,
    merchantName: honeyApiResponse.name,
    merchantDescription: honeyStoreDetails.description,
    availableOffers: totalOffers,
    averageSavings,
    couponCodes: totalCoupons,
    bestHoneyCoupons
  }

  const storePageTitle = formatMessage('main.storePage.title', {
    name: honeyApiResponse.name
  })
  const allOfferIds = allOfferIdsArray.join(',')
  const offerCount = JSON.stringify({
    deals: dealOffers.length,
    codes: couponOffers.length
  })

  const contentClassName = isDW2
    ? 'store-page__content--DW2'
    : 'store-page__content'

  const contentStyle = isUserLoggedIn
    ? contentClassName
    : 'store-page__content--log-out'

  useEffect(() => {
    sendFptiImpression({
      event_name: SHOPPING_MERCHANT_SCREEN_SHOWN,
      offer_id: allOfferIds,
      offer_count: offerCount
    })
  }, [allOfferIds, offerCount])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [initialModalLaunched, setInitialModalLaunched] = useState(false)

  useEffect(() => {
    const isModalOpenFlag = get(requestUrl, 'query.isModalOpen')
    if (isModalOpenFlag === 'true' && !initialModalLaunched) {
      setIsModalOpen(true)
      setInitialModalLaunched(true)
      setAffiliateVisited(true)
    }
    // TODO: investigate how to call useEffect just once without using empty array []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const storeAffiliateLink = getAffiliateLink({
    honeyStoreId: honeyStoreDetails.storeId,
    fptiSessionId,
    turl: honeyStoreDetails.url,
    encryptedAccountNumber
  })

  return (
    <div className="slnw-layout">
      {isTMPlus && <StorePageHeader />}
      <div className="store-page">
        <div className={contentStyle}>
          <div className="store-page__store-details">
            <StoreDetails data={storeData} />
          </div>
          <div className="store-page__offer-details">
            <div className="store-page__offer-details-header">
              <div className="store-details__logo-container">
                {isTablet || isMobile ? (
                  <LogoImage
                    src={honeyStoreDetails.logo}
                    defaultSrc={defaultMerchantLogo}
                    alt={honeyApiResponse.name}
                    className="store-details__logo-image"
                  />
                ) : null}
                <h1 className="store-page__title">{storePageTitle}</h1>
              </div>
              <div
                className="store-details__merchant-description"
                data-cy="storeDescription"
              >
                <Message
                  id="main.storePage.subHeaderText"
                  merchantName={honeyApiResponse.name}
                  link={(innerMessage: string) => (
                    <a
                      href={storeAffiliateLink}
                      target="__blank"
                      rel="noreferrer"
                    >
                      {innerMessage}
                    </a>
                  )}
                />
              </div>
            </div>
            <OfferTabs
              storeId={storeData.storeId}
              allOffers={allOffers}
              affiliateVisited={affiliateVisited}
              urlProperties={requestUrl.query}
              fptiSessionId={fptiSessionId}
              encryptedAccountNumber={encryptedAccountNumber}
            />
          </div>

          <div className="store-page__sidebar-details">
            <StoreDetailsSidebarCard
              title={formatMessage('main.storePage.details.about', {
                merchantName: storeData.merchantName
              })}
              subHeader={storeData.merchantDescription}
            />
          </div>
        </div>
        {isUserLoggedIn && <LogoutModal />}
      </div>
      <HoneyOfferDetailsModal
        csrf={csrf}
        isOpen={isModalOpen}
        onClose={() => {
          if (initialModalLaunched) {
            setIsModalOpen(false)
          }
        }}
        honeyOfferId={get(requestUrl, 'query.offerId') || ''}
        honeyStoreId={get(serverData, 'honeyStoreDetails.storeId') || ''}
        offerType={get(requestUrl, 'query.offerType') || ''}
        breakpoint={useBreakpoint()}
        affiliateTagUrl={get(
          requestUrl,
          'query.affiliateLink',
          storeAffiliateLink
        )} // temporary property for affiliate link frontend fix story: DTSHOPATMS-1503
      />
    </div>
  )
}

export default StorePage
